import { ApiError, getMessage, request } from './client';
import { parseUserRow, usersEnvelopeSchema } from '../components/users/userSchema';
import { toQueryString } from '../components/users/usersParams';
import { parseProfile } from '../components/users/profileSchema';
import { rewardsSchema } from '../components/users/rewardSchema';

/**
 * Admin users API. Always hits the real backend.
 *
 * GET admin/users?page&limit&search&status&fuelType&sortBy&sortOrder
 * → { Success, Message, Data: { users, pagination, totalRegistered } }
 */

/**
 * @param {import('../components/users/usersParams').UsersParams} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ users: import('../components/users/userSchema').UserDto[],
 *   pagination: { page: number, limit: number, totalItems: number, totalPages: number,
 *   hasNextPage: boolean, hasPrevPage: boolean }, totalRegistered: number }>}
 */
export async function list(params, { signal } = {}) {
  const query = toQueryString(params, { includeDefaults: true });
  const res = await request(`admin/users?${query}`, { signal });

  const envelope = usersEnvelopeSchema.safeParse(res?.Data);
  if (!envelope.success) throw new ApiError('Unexpected response from the server.', 200, res);

  const users = envelope.data.users.map((raw, index) => {
    const { row, invalidFields } = parseUserRow(raw);
    if (invalidFields.length) {
      // Field names and position only: never log the row itself (PII)
      console.warn(`[users] row ${index} has invalid fields: ${invalidFields.join(', ')}`);
    }
    return row;
  });

  return { users, pagination: envelope.data.pagination, totalRegistered: envelope.data.totalRegistered };
}

/**
 * POST user/get-profile { userId } → { Success, Message, Data: profile }.
 * Only allow-listed fields are kept (see profileSchema.js): the raw response also
 * contains the password hash and salt, which must never be stored or rendered.
 * @returns {Promise<import('../components/users/profileSchema').ProfileDto>}
 */
export async function getProfile(userId, { signal } = {}) {
  const res = await request('user/get-profile', { method: 'POST', body: { userId }, signal });
  if (!res?.Data || typeof res.Data !== 'object') throw new ApiError('Unexpected response from the server.', 200, null);
  const { profile, invalidFields } = parseProfile(res.Data);
  if (!profile.userId) throw new ApiError('Unexpected response from the server.', 200, null);
  if (invalidFields.length) console.warn(`[users] profile has invalid fields: ${invalidFields.join(', ')}`);
  return profile;
}

/**
 * GET admin/users/:userId/rewards (the public POM id) → the reward ladder for that user.
 * @returns {Promise<import('zod').infer<typeof rewardsSchema>>}
 */
export async function getUserRewards(userId, { signal } = {}) {
  const res = await request(`admin/users/${encodeURIComponent(userId)}/rewards`, { signal });
  const parsed = rewardsSchema.safeParse(res?.Data);
  if (!parsed.success) throw new ApiError('Unexpected response from the server.', 200, null);
  return parsed.data;
}

/** Only for the button on the profile page, which isn't connected yet. The list menu uses `suspend()`. */
export const SUSPEND_AVAILABLE = false;

/**
 * DELETE users/:userId (the public POM id) → { status, message, data: { userId } }.
 * Only sets isActive to false. Failures can be 2xx (290 = not found or already inactive),
 * so success is status === true in the body and nothing else.
 */
export async function suspend(userId) {
  const res = await request(`users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  if (res?.status !== true) throw new ApiError(getMessage(res) ?? 'Unexpected response from the server.', 200, res);
  return res;
}

export const usersApi = { list, getProfile, getUserRewards, suspend };
